import { useEffect, useRef, useState } from "react";

export interface FilterOption {
  label: string;
  value: string;
}

interface DashboardFilterChipProps {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
}

export function DashboardFilterChip({
  label,
  value,
  options,
  onChange,
}: DashboardFilterChipProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;

    function handleOutsideClick(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef} data-oid="43ze3hj">
      <button
        ref={triggerRef}
        className={[
          "dashboard-filter-chip inline-flex h-9 items-center gap-1.5 rounded-lg border px-2.5 transition-colors",
          open
            ? "border-sky-500/40 bg-slate-800"
            : "border-white/10 bg-slate-900 hover:border-slate-600 hover:bg-slate-800",
        ].join(" ")}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        data-oid="_n7bwxc"
      >
        <span className="filter-label text-slate-500" data-oid="-ih26zg">
          {label}:
        </span>
        <span className="filter-value text-slate-100" data-oid="a5r9r93">
          {selectedOption?.label ?? value}
        </span>
        <span
          aria-hidden="true"
          className={[
            "filter-chevron material-symbols-outlined text-[11px] text-slate-500 transition-transform duration-150",
            open ? "rotate-180" : "",
          ].join(" ")}
          data-oid="gagzpv9"
        >
          expand_more
        </span>
      </button>

      {open ? (
        <ul
          role="listbox"
          className="dashboard-filter-dropdown absolute left-0 top-full z-50 mt-1.5 min-w-[168px] rounded-xl border border-white/10 bg-slate-900 py-1 shadow-[0_8px_32px_rgba(0,0,0,0.45)]"
          style={{ minWidth: triggerRef.current?.offsetWidth }}
          data-oid="zwvnsdq"
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={isSelected}
                data-oid="iireba2"
              >
                <button
                  className={[
                    "typography-body flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors",
                    isSelected
                      ? "text-sky-300"
                      : "text-slate-300 hover:bg-white/5 hover:text-white",
                  ].join(" ")}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  data-oid="7wsma7b"
                >
                  <span
                    aria-hidden="true"
                    className={[
                      "material-symbols-outlined shrink-0 text-[13px] text-sky-400",
                      isSelected ? "opacity-100" : "opacity-0",
                    ].join(" ")}
                    data-oid="ydtzbys"
                  >
                    check
                  </span>
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
